import { default as logger } from '../common/log'
import { actionCreatorsFor } from '../common/redux-crud-actions'
import { DefaultPushConfiguration } from './push-configuration'
import { PushDataThrottler } from './push-data-throttler'
import { PUSH_PROTOCOL } from './push-protocol'
import { SOCKET_EVENTS } from './socket-events'
import { storeSocketInstance } from './socket'

/**
 * Global push action that any reducer may handle
 */

//
// Redux action types (exported constants are needed by the corresponding store)
//
export const PUSH_OPENED = 'PUSH_OPENED'
export const PUSH_CLOSED = 'PUSH_CLOSED'
export const PUSH_STARTED = 'PUSH_STARTED'
export const PUSH_STOPPED = 'PUSH_STOPPED'

/**
 * Returns an action creator thunk that opens a WebSocket.
 *
 * @param {PushConfiguration} configuration
 * @return {Function}
 */
function pushOpen(configuration) {
    let startImmediately = configuration.startImmediately

    // The returned promise resolves to true once the socket is opened.
    return function (dispatch, getState) {
        // Check if /socket.io/socket.io.js is included in index.html and server supports push
        if (!configuration.sioClient) {
            logger.warn('socket.io is not available, cannot enable push')
            throw new Error('socket.io is not available')
        }

        if (getState().socket != null && getState().pushState.opened) {
            // Do not open the socket twice.
            return Promise.resolve(true)
        }

        return new Promise((resolve, reject) => {
            /**
             * Factory, which returns a store update function for fetch or patch responses
             */
            const updateStores = usePatch => data => {
                const { pushState } = getState()
                if (!pushState.opened || !pushState.pushing) {
                    return
                }
                // Create an action for each resource available in the push data and dispatch that action
                Object.keys(data).forEach(resourceName => {
                    const actionCreators = getActionCreators(resourceName, data)
                    if (usePatch) {
                        dispatch(actionCreators.patchSuccess)
                    } else {
                        dispatch(actionCreators.fetchSuccess)
                    }
                })
            }

            const updateStoresPatch = updateStores(true)
            const updateStoresFetch = updateStores(false)

            const pushDataThrottler = new PushDataThrottler(
                data => {
                    updateStoresPatch(data)
                },
                configuration.getThrottlingIntervalInMs,
                getState
            )

            // Connect to WebSocket
            const socket = configuration.sioClient(configuration.pushUrl, configuration.sioClientOptions)
            // Store socket in redux store to have it accessible across micro-frontends
            dispatch(storeSocketInstance(socket))

            // After a successful reconnection the `connect` event is thrown,
            // so we don't need to handle the `reconnect` event.
            socket.on(SOCKET_EVENTS.CONNECT, () => {
                pushDataThrottler.start()
                dispatch(pushOpened())

                // Send the push-schedules configuration to the server
                const schedules = getState().schedules
                if (schedules && Object.keys(schedules).length > 0) {
                    socket.emit(PUSH_PROTOCOL.COMMAND_CONFIG, schedules)
                }

                // Start pushing immediately if the web configuration delivered by ATAPI wants that.
                // Also, if the previous state was 'pushing', the connection was interrupted,
                // or the server died, then restore the old state by firing a pushStart action.
                if (startImmediately || getState().pushState.pushing) {
                    logger.info(
                        'Start pushing ',
                        startImmediately ? 'as requested by configuration' : 'after reconnection'
                    )
                    dispatch(pushStart())
                    startImmediately = false // Do not use the parameter on re-connection (not: JS passes by value)
                }

                // Resolve the promise to allow chaining
                resolve(true)
            })

            socket.on(SOCKET_EVENTS.DISCONNECT, () => {
                pushDataThrottler.stop()
                dispatch(pushClosed())
            })

            socket.on(SOCKET_EVENTS.CONNECT_ERROR, error => {
                // Reject promise if connection could not be established
                logger.warn(`Unable to connect socket: ${JSON.stringify(error)}`)
                reject(error)
            })

            // Dispatch the received data to all reducers
            socket.on(PUSH_PROTOCOL.EVENT_DATA, data => {
                const json = JSON.parse(data)
                // To avoid race conditions between scheduler and push, we define the data from the scheduler
                // as "the truth" and throw away all data we received via push in the meantime.
                updateStoresFetch(json)
                pushDataThrottler.discard(Object.keys(json))
            })

            // Same for patch data coming from Push Server
            socket.on(PUSH_PROTOCOL.EVENT_PATCH, data => pushDataThrottler.collect(data))
        })
    }
}

function pushOpened() {
    return { type: PUSH_OPENED }
}

function pushClosed() {
    return { type: PUSH_CLOSED }
}

function pushStart() {
    return function (dispatch, getState) {
        const { pushState, socket } = getState()
        if (socket != null && pushState.opened /* && !pushState.pushing*/) {
            socket.emit(PUSH_PROTOCOL.COMMAND_START)
            // The start command may not reach the server, but in this case, simply no data will arrive.
            // This may need improvement, but at the moment, we can live with that.
            dispatch(pushStarted())
        }
    }
}

function pushStarted() {
    logger.log('Started pushing')
    return { type: PUSH_STARTED }
}

function pushStop() {
    return function (dispatch, getState) {
        const { pushState, socket } = getState()
        if (socket != null && pushState.opened /* && pushState.pushing*/) {
            socket.emit(PUSH_PROTOCOL.COMMAND_STOP)
            // The stop command may not reach the server, but then the 'pushing' flag will suppress
            // any data that is still arriving from the server.
            dispatch(pushStopped())
        }
    }
}

function pushStopped() {
    logger.log('Stopped pushing')
    return { type: PUSH_STOPPED }
}

// See Dan's example of store.subscribe at http://redux.js.org/docs/api/Store.html#subscribe
// TODO: Is there a better way of reacting on real store changes than having a local variable?
function subscribeToStore(store) {
    let currSchedules

    function handleChange() {
        const { pushState = { opened: false }, schedules = {}, socket } = store.getState()
        const socketOpen = pushState.opened
        const prevSchedules = currSchedules
        currSchedules = schedules
        // If the socket is not open yet, we cannot emit the configuration. It will be sent on SOCKET_CONNECT.
        // Moreover, send the configuration only if it changed and it is not empty.
        if (socket != null && socketOpen && prevSchedules !== currSchedules && Object.keys(currSchedules).length > 0) {
            logger.info('Refresh-schedules configuration changed, send configuration')
            socket.emit(PUSH_PROTOCOL.COMMAND_CONFIG, currSchedules)
        }
    }

    return store.subscribe(handleChange)
}

// Store of all already created fetchSuccess action creators
const actionCreatorMap = {}

/**
 * Returns action creators for the given resource name. Action creators are cached to avoid the expensive recreation.
 *
 * @param {string} resourceName - The name of the resource for which the action creator is requested.
 */
function getActionCreators(resourceName, data) {
    let actionCreators = actionCreatorMap[resourceName]
    if (!actionCreators) {
        actionCreators = actionCreatorsFor(resourceName)
        actionCreatorMap[resourceName] = actionCreators
    }
    return {
        fetchSuccess: dispatch => {
            return dispatch(actionCreators.fetchSuccess(data[resourceName], `push/${resourceName}`, false))
        },

        patchSuccess: dispatch => {
            return dispatch(actionCreators.patchMergePatchJsonSuccess(data[resourceName]))
        }
    }
}

export const pushActions = {
    open: pushOpen,
    start: pushStart,
    stop: pushStop,
    subscribe: subscribeToStore
}

export function createConfigurePushThunk(pushThrottlingIntervalInMs) {
    // Open a socket.io connection
    const pushConfig = new DefaultPushConfiguration()
    pushConfig.getThrottlingIntervalInMs = () => pushThrottlingIntervalInMs

    return function openPush(dispatch) {
        return dispatch(pushActions.open(pushConfig))
    }
}
